import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ChecklistCard from './components/ChecklistCard';
import ProgressOverview from './components/ProgressOverview';
import AchievementBadge from './components/AchievementBadge';

const ProfileSetupChecklist = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProgress();
    }
  }, [user]);

  // Automatic routing when profile setup is complete
  useEffect(() => {
    if (progress?.overall_percentage === 100 && userProfile?.role) {
      // Add a small delay for better UX (user can see the completion state)
      const timer = setTimeout(() => {
        const dashboardRoute = userProfile?.role === 'artist' ? '/artist-dashboard' : '/influencer-dashboard';
        navigate(dashboardRoute, { replace: true });
      }, 2000); // 2 second delay to show completion message

      return () => clearTimeout(timer);
    }
  }, [progress?.overall_percentage, userProfile?.role, navigate]);

  // Refresh progress when user returns to this page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchProgress();
      }
    };

    const handleFocus = () => {
      if (user) {
        fetchProgress();
      }
    };

    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for window focus (returning from another page)
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('profile_progress')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

      if (fetchError) {
        if (isSchemaError(fetchError)) {
          console.error('Schema error:', fetchError?.message);
          throw fetchError;
        } else if (fetchError?.code === 'PGRST116') {
          setProgress(null);
        } else {
          console.log('Fetch error:', fetchError?.message);
          setError(fetchError?.message);
        }
      } else {
        setProgress(data);
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
      setError(err?.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') {
        return true;
      }
      if (errorClass === '23') {
        return false;
      }
    }
    if (error?.message) {
      const schemaErrorPatterns = [
        /relation.*does not exist/i,
        /column.*does not exist/i,
        /function.*does not exist/i,
        /syntax error/i,
        /invalid.*syntax/i,
        /type.*does not exist/i,
        /undefined.*column/i,
        /undefined.*table/i,
        /undefined.*function/i,
      ];
      return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
    }
    return false;
  };

  const updateProgress = async (field, value) => {
    try {
      const { data, error: updateError } = await supabase?.from('profile_progress')?.update({ [field]: value })?.eq('user_id', user?.id)?.select()?.single();

      if (updateError) {
        if (isSchemaError(updateError)) {
          console.error('Schema error:', updateError?.message);
          throw updateError;
        } else {
          console.log('Update error:', updateError?.message);
          setError(updateError?.message);
        }
      } else {
        setProgress(data);
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
      setError(err?.message || 'Failed to update progress');
    }
  };

  const checklistItems = [
    {
      id: 'profile_info',
      title: 'Profile Information',
      description: 'Complete your basic details, bio, and genre selection',
      icon: 'User',
      completed: progress?.profile_info_completed || false,
      route: userProfile?.role === 'artist' ? '/artist-profile-creation' : '/influencer-profile-creation',
      estimatedTime: '5 min',
      priority: 'high',
      tasks: [
        'Add your name and profile picture',
        'Write a compelling bio',
        'Select your music genres or content categories',
        'Set your budget or pricing'
      ]
    },
    {
      id: 'social_links',
      title: 'Social Media Links',
      description: 'Connect your social media accounts with verification badges',
      icon: 'Share2',
      completed: progress?.social_links_completed || false,
      route: '/social-links-setup',
      estimatedTime: '3 min',
      priority: 'high',
      tasks: [
        'Link Instagram account',
        'Link YouTube channel',
        'Link TikTok profile',
        'Link Twitter/X account'
      ]
    },
    {
      id: 'verification',
      title: 'Account Verification',
      description: 'Verify your identity and business registration',
      icon: 'ShieldCheck',
      completed: progress?.verification_completed || false,
      route: userProfile?.role === 'artist' ? '/artist-profile-creation' : '/influencer-profile-creation',
      estimatedTime: '10 min',
      priority: 'medium',
      tasks: [
        'Upload identity verification documents',
        'Complete business registration (if applicable)',
        'Verify email address',
        'Verify phone number'
      ]
    },
    {
      id: 'payment_methods',
      title: 'Payment Methods',
      description: 'Set up banking details and commission preferences',
      icon: 'CreditCard',
      completed: progress?.payment_methods_completed || false,
      route: '/payment-setup',
      estimatedTime: '7 min',
      priority: 'medium',
      tasks: [
        'Add bank account details',
        'Set payment preferences',
        'Configure commission rates',
        'Review payment terms'
      ]
    }
  ];

  const completedCount = checklistItems?.filter(item => item?.completed)?.length;
  const totalCount = checklistItems?.length;
  const overallPercentage = progress?.overall_percentage || 0;

  const getMotivationalMessage = () => {
    if (overallPercentage === 100) {
      return "🎉 Congratulations! Your profile is complete!";
    } else if (overallPercentage >= 75) {
      return "Almost there! Just a few more steps to go.";
    } else if (overallPercentage >= 50) {
      return "Great progress! You're halfway there.";
    } else if (overallPercentage >= 25) {
      return "Good start! Keep going to unlock all features.";
    } else {
      return "Welcome! Let's get your profile set up.";
    }
  };

  const getEstimatedTimeRemaining = () => {
    const remainingItems = checklistItems?.filter(item => !item?.completed);
    const totalMinutes = remainingItems?.reduce((sum, item) => {
      const minutes = parseInt(item?.estimatedTime?.split(' ')?.[0]) || 0;
      return sum + minutes;
    }, 0);
    return totalMinutes > 0 ? `${totalMinutes} min remaining` : 'All done!';
  };

  const achievements = [
    {
      id: 'first_step',
      title: 'First Step',
      description: 'Complete your first checklist item',
      icon: 'Award',
      unlocked: completedCount >= 1
    },
    {
      id: 'halfway',
      title: 'Halfway Hero',
      description: 'Complete 50% of your profile',
      icon: 'Star',
      unlocked: overallPercentage >= 50
    },
    {
      id: 'profile_master',
      title: 'Profile Master',
      description: 'Complete 100% of your profile',
      icon: 'Trophy',
      unlocked: overallPercentage === 100
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading your progress...</p>
            </div>
          </div>
        </main>
        <MobileNav />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-error" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Progress</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchProgress} iconName="RefreshCw">
                Try Again
              </Button>
            </div>
          </div>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Back</span>
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Profile Setup Checklist
              </h1>
              <p className="text-muted-foreground">
                Complete these steps to unlock all features and maximize your profile visibility
              </p>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <ProgressOverview
          percentage={overallPercentage}
          completedCount={completedCount}
          totalCount={totalCount}
          motivationalMessage={getMotivationalMessage()}
          estimatedTime={getEstimatedTimeRemaining()}
        />

        {/* Checklist Items */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Setup Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {checklistItems?.map((item) => (
              <ChecklistCard
                key={item?.id}
                item={item}
                onNavigate={() => navigate(item?.route)}
              />
            ))}
          </div>
        </div>

        {/* Achievement Badges */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Achievements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {achievements?.map((achievement) => (
              <AchievementBadge
                key={achievement?.id}
                achievement={achievement}
              />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        {overallPercentage === 100 ? (
          <div className="bg-success/10 border border-success/20 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle2" size={32} className="text-success" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Profile Complete!
            </h3>
            <p className="text-muted-foreground mb-4">
              Your profile is fully set up. You can now explore all features and start connecting with {userProfile?.role === 'artist' ? 'influencers' : 'artists'}.
            </p>
            <Button
              onClick={() => navigate(userProfile?.role === 'artist' ? '/artist-dashboard' : '/influencer-dashboard')}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Go to Dashboard
            </Button>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Need Help?
            </h3>
            <p className="text-muted-foreground mb-4">
              Our support team is here to assist you with profile setup and any questions you may have.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" iconName="HelpCircle">
                View Guide
              </Button>
              <Button variant="outline" iconName="MessageCircle">
                Contact Support
              </Button>
            </div>
          </div>
        )}
      </main>

      <MobileNav />
    </div>
  );
};

export default ProfileSetupChecklist;